type Message = { type: string; [k: string]: unknown };
type Listener = (msg: Message) => void;

const WS_URL = (import.meta.env.VITE_WS_URL as string) || "";

class WebSocketManager {
  private ws: WebSocket | null = null;
  private listeners = new Map<string, Set<Listener>>();
  private reconnectAttempts = 0;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private connectingPath: string | null = null;

  connect(path: string) {
    this.connectingPath = path;
    this._open();
  }

  private _open() {
    if (!this.connectingPath) return;
    // Browsers send the access_token cookie automatically when the WS
    // endpoint is on the same eTLD+1 as the page (Domain=.hermys.ai). The
    // backend also accepts ?token=<jwt> for native clients.
    const url = `${WS_URL}${this.connectingPath}`;
    this.ws = new WebSocket(url);
    this.ws.onopen = () => {
      this.reconnectAttempts = 0;
      this._startHeartbeat();
      this._dispatch({ type: "ws.open" });
    };
    this.ws.onmessage = (e) => {
      try {
        this._dispatch(JSON.parse(e.data));
      } catch {
        // Drop non-JSON frames silently.
      }
    };
    this.ws.onclose = () => this._onClose();
    this.ws.onerror = () => this.ws?.close();
  }

  private _onClose() {
    this._stopHeartbeat();
    this._dispatch({ type: "ws.close" });
    if (!this.connectingPath) return;
    const delay = Math.min(30_000, 500 * 2 ** this.reconnectAttempts);
    this.reconnectAttempts += 1;
    setTimeout(() => this._open(), delay);
  }

  private _startHeartbeat() {
    this.heartbeatTimer = setInterval(() => {
      try {
        this.ws?.send(JSON.stringify({ type: "pong" }));
      } catch {
        // socket closed mid-heartbeat — onclose handles reconnect.
      }
    }, 25_000);
  }

  private _stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private _dispatch(msg: Message) {
    const listeners = this.listeners.get(msg.type);
    if (!listeners) return;
    for (const fn of listeners) fn(msg);
  }

  on(type: string, fn: Listener): () => void {
    let set = this.listeners.get(type);
    if (!set) {
      set = new Set();
      this.listeners.set(type, set);
    }
    set.add(fn);
    return () => {
      set!.delete(fn);
    };
  }

  disconnect() {
    this.connectingPath = null;
    this._stopHeartbeat();
    this.ws?.close();
    this.ws = null;
  }
}

export const wsManager = new WebSocketManager();
